/**
 * Buttons (prompt.md §37, §40).
 *
 * WHY the variants are named for intent (`primary`, `danger`) rather than for
 * appearance: a danger action must be recognisable as destructive in the web
 * app *and* in the Tauri shell without either screen picking its own red.
 *
 * Hover and focus are expressed as a CSS custom property plus a stylesheet class
 * (see `theme/stylesheet.ts`) rather than by writing to the DOM in a handler, so
 * the states also work for keyboard users and under reduced motion.
 */

import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from "react";

import { controlStyle, type ControlSize, type ControlVariant, hoverPalette } from "../styles.ts";
import { CLASS_NAMES, STATE_VARIABLES } from "../theme/stylesheet.ts";
import { Spinner } from "./Feedback.tsx";

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className" | "style"> {
  readonly variant?: ControlVariant;
  readonly size?: ControlSize;
  /** Shows a spinner and blocks interaction while an action is in flight. */
  readonly loading?: boolean;
  readonly fullWidth?: boolean;
  readonly icon?: ReactNode;
}

export function Button({ variant = "secondary", size = "md", loading = false, fullWidth = false, icon, disabled, children, type = "button", ...rest }: ButtonProps) {
  const isDisabled = disabled === true || loading;
  const palette = hoverPalette(variant);
  return (
    <button
      {...rest}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={[CLASS_NAMES.control, CLASS_NAMES.focusable].join(" ")}
      style={
        {
          ...controlStyle({ variant, size, disabled: isDisabled, fullWidth }),
          // The variant decides the colours; the stylesheet decides when they
          // apply. Writing them as custom properties is what keeps hover and
          // active working for keyboard users too.
          [STATE_VARIABLES.hoverBackground]: palette.hover,
          [STATE_VARIABLES.activeBackground]: palette.active,
        } as CSSProperties
      }
    >
      {loading ? <Spinner size="sm" label={null} /> : icon}
      {children}
    </button>
  );
}

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className" | "style" | "children"> {
  /** Required: an icon-only control must still be readable to a screen reader. */
  readonly label: string;
  readonly variant?: ControlVariant;
  readonly size?: ControlSize;
  readonly children: ReactNode;
}

export function IconButton({ label, variant = "ghost", size = "md", children, ...rest }: IconButtonProps) {
  const palette = hoverPalette(variant);
  return (
    <button
      {...rest}
      type={rest.type ?? "button"}
      aria-label={label}
      title={label}
      className={[CLASS_NAMES.control, CLASS_NAMES.focusable].join(" ")}
      style={
        {
          ...controlStyle({ variant, size, disabled: rest.disabled === true, iconOnly: true }),
          [STATE_VARIABLES.hoverBackground]: palette.hover,
        } as CSSProperties
      }
    >
      {children}
    </button>
  );
}
